import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CSVImportModal from './CSVImportModal';
import Pagination from './Pagination';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ClientsCRMEnhanced = () => {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  
  // Advanced search and filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterIndustry, setFilterIndustry] = useState('');
  const [filterCommercial, setFilterCommercial] = useState('');
  // Sprint 6 - Nouveaux filtres
  const [filterTypeContact, setFilterTypeContact] = useState('');
  const [filterCity, setFilterCity] = useState('');
  const [filterPays, setFilterPays] = useState('');
  const [sortField, setSortField] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  
  // Batch operations
  const [selectedClients, setSelectedClients] = useState([]);
  const [showBatchActions, setShowBatchActions] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  
  // Sprint 7 - Export CSV state
  const [exportLoading, setExportLoading] = useState(false);
  
  // Lists for filters
  const [commerciaux, setCommerciaux] = useState([]);
  const [industries, setIndustries] = useState([]);
  
  useEffect(() => {
    fetchClients();
    fetchCommerciaux();
  }, []);

  useEffect(() => {
    setShowBatchActions(selectedClients.length > 0);
    // Extract unique industries from clients
    const uniqueIndustries = [...new Set(clients.map(c => c.industry).filter(Boolean))];
    setIndustries(uniqueIndustries);
  }, [selectedClients, clients]);

  const fetchClients = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/crm/clients`);
      setClients(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCommerciaux = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setCommerciaux(response.data);
    } catch (error) {
      console.error('Error fetching commerciaux:', error);
    }
  };

  const reloadData = async () => {
    await fetchClients();
    setSelectedClients([]);
  };

  // Advanced search function with Sprint 6 filters
  const handleAdvancedSearch = async () => {
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('query', searchTerm);
      if (filterStatus) params.append('status', filterStatus);
      if (filterIndustry) params.append('industry', filterIndustry);
      if (filterCommercial) params.append('commercial_id', filterCommercial);
      // Sprint 6 - Nouveaux filtres
      if (filterTypeContact) params.append('type_contact', filterTypeContact);
      if (filterCity) params.append('city', filterCity);
      if (filterPays) params.append('pays', filterPays);
      
      const response = await axios.get(`${API}/crm/clients/search?${params}`);
      setClients(response.data);
      setSelectedClients([]);
    } catch (error) {
      console.error('Error in advanced search:', error);
      // Fallback to client-side filtering
      const filtered = getFilteredAndSortedClients();
      setClients(filtered);
    }
  };

  const resetFilters = () => {
    setSearchTerm('');
    setFilterStatus('');
    setFilterIndustry('');
    setFilterCommercial('');
    // Sprint 6 - Reset nouveaux filtres
    setFilterTypeContact('');
    setFilterCity('');
    setFilterPays('');
    setSortField('name');
    setSortDirection('asc');
    setSelectedClients([]);
    fetchClients();
  };

  // Sprint 7 - Export CSV function
  const handleExportCSV = async () => {
    try {
      setExportLoading(true);
      
      // Construire les paramètres avec les filtres appliqués
      const params = new URLSearchParams();
      if (searchTerm) params.append('query', searchTerm);
      if (filterStatus) params.append('status', filterStatus);
      if (filterIndustry) params.append('industry', filterIndustry);
      if (filterTypeContact) params.append('type_contact', filterTypeContact);
      if (filterCity) params.append('city', filterCity);
      if (filterPays) params.append('pays', filterPays);
      
      const response = await axios.get(`${API}/export/clients/csv?${params}`, {
        responseType: 'blob'
      });
      
      // Créer le lien de téléchargement
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Générer le nom de fichier avec timestamp
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '');
      link.download = `societes_baobab_connect_${timestamp}.csv`;
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      alert('Export CSV téléchargé avec succès !');
      
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Erreur lors de l\'export CSV');
    } finally {
      setExportLoading(false);
    }
  };

  // Client-side filtering and sorting
  const getFilteredAndSortedClients = () => {
    let filtered = clients.filter(client => {
      const matchesSearch = !searchTerm || 
        client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.phone?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        client.responsable_compte_nom?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = !filterStatus || client.status === filterStatus;
      const matchesIndustry = !filterIndustry || client.industry === filterIndustry;
      const matchesCommercial = !filterCommercial || client.responsable_compte_id === filterCommercial;
      
      return matchesSearch && matchesStatus && matchesIndustry && matchesCommercial;
    });

    // Sort clients
    filtered.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';
      
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

    return filtered;
  };

  // Selection handlers
  const handleSelectClient = (clientId) => {
    setSelectedClients(prev => {
      if (prev.includes(clientId)) {
        return prev.filter(id => id !== clientId);
      } else {
        return [...prev, clientId];
      }
    });
  };

  const handleSelectAll = () => {
    const filteredClients = getFilteredAndSortedClients();
    if (selectedClients.length === filteredClients.length) {
      setSelectedClients([]);
    } else {
      setSelectedClients(filteredClients.map(client => client.id));
    }
  };

  // Batch operations
  const handleBatchDelete = async () => {
    if (selectedClients.length === 0) return;
    
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer ${selectedClients.length} société(s) ?`)) {
      try {
        await axios.post(`${API}/crm/clients/batch-delete`, {
          client_ids: selectedClients
        });
        
        fetchClients();
        setSelectedClients([]);
        alert(`${selectedClients.length} société(s) supprimée(s) avec succès!`);
      } catch (error) {
        console.error('Error in batch delete:', error);
        alert('Erreur lors de la suppression multiple');
      }
    }
  };

  const handleBatchStatusChange = async (newStatus) => {
    if (selectedClients.length === 0) return;
    
    try {
      await axios.post(`${API}/crm/clients/batch-update-status`, {
        client_ids: selectedClients,
        status: newStatus
      });
      
      fetchClients();
      setSelectedClients([]);
      
      const statusLabels = {
        'active': 'actives',
        'inactive': 'inactives',
        'prospect': 'prospects'
      };
      
      alert(`${selectedClients.length} société(s) marquée(s) comme ${statusLabels[newStatus]} avec succès!`);
    } catch (error) {
      console.error('Error in batch status update:', error);
      alert('Erreur lors de la modification du statut');
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleSubmit = async (clientData) => {
    try {
      // Validation côté frontend
      if (!clientData.name || clientData.name.trim() === '') {
        alert('Le nom de la société est obligatoire');
        return;
      }
      
      if (editingClient) {
        await axios.put(`${API}/crm/clients/${editingClient.id}`, clientData);
      } else {
        await axios.post(`${API}/crm/clients`, clientData);
      }
      fetchClients();
      setShowForm(false);
      setEditingClient(null);
      alert('Société ' + (editingClient ? 'modifiée' : 'créée') + ' avec succès!');
    } catch (error) {
      console.error('Error saving client:', error);
      
      // Améliorer les messages d'erreur
      let errorMessage = 'Erreur lors de la sauvegarde de la société';
      
      if (error.response?.status === 422) {
        const errorDetails = error.response.data;
        if (typeof errorDetails === 'string') {
          errorMessage = errorDetails;
        } else if (errorDetails.detail) {
          if (Array.isArray(errorDetails.detail)) {
            errorMessage = errorDetails.detail.map(err => err.msg || err).join(', ');
          } else {
            errorMessage = errorDetails.detail;
          }
        }
      } else if (error.response?.status === 400) {
        errorMessage = 'Données invalides. Vérifiez les informations saisies.';
      } else if (error.response?.status === 409) {
        errorMessage = 'Cette société existe déjà. Utilisez un nom différent.';
      } else if (!error.response) {
        errorMessage = 'Impossible de contacter le serveur. Vérifiez votre connexion internet.';
      }
      
      alert(errorMessage);
    }
  };

  const handleDelete = async (clientId) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette société ?')) {
      try {
        await axios.delete(`${API}/crm/clients/${clientId}`);
        fetchClients();
        alert('Société supprimée avec succès!');
      } catch (error) {
        console.error('Error deleting client:', error);
        alert('Erreur lors de la suppression de la société');
      }
    }
  };

  const filteredClients = getFilteredAndSortedClients();

  const getSortIcon = (field) => {
    if (sortField !== field) return '↕️';
    return sortDirection === 'asc' ? '↑' : '↓';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-red-100 text-red-800';
      case 'prospect':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    const labels = {
      'active': 'Actif',
      'inactive': 'Inactif',
      'prospect': 'Prospect'
    };
    return labels[status] || status;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="spinner mb-4"></div>
          <p className="text-gray-600">Chargement des sociétés...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Gestion des Sociétés</h1>
        <div className="flex gap-2">
          {/* Reload Data Button */}
          <button
            onClick={reloadData}
            disabled={loading}
            className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg transition-colors text-sm border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Actualiser les données"
          >
            {loading ? (
              <div className="flex items-center">
                <div className="spinner-sm mr-1"></div>
                <span className="hidden sm:inline">Chargement...</span>
              </div>
            ) : (
              <div className="flex items-center">
                <span className="text-base mr-1">🔄</span>
                <span className="hidden sm:inline">Reload</span>
              </div>
            )}
          </button>
          
          {/* Import CSV Button */}
          <button
            onClick={() => setShowImportModal(true)}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors text-sm"
          >
            <div className="flex items-center">
              <span className="mr-1">📄</span>
              <span className="hidden sm:inline">Import CSV</span>
            </div>
          </button>
          
          {/* New Company Button */}
          <button
            onClick={() => setShowForm(true)}
            className="bg-baobab-primary text-white px-4 py-2 rounded-lg hover:bg-baobab-dark transition-colors"
          >
            Nouvelle Société
          </button>
        </div>
      </div>

      {/* Advanced Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <h3 className="font-medium text-gray-900 mb-4 flex items-center">
          <span className="mr-2">🔍</span>
          Recherche Avancée
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-8 gap-4 mb-4">
          <div>
            <input
              type="text"
              placeholder="Nom, email, téléphone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
            />
          </div>
          
          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
            >
              <option value="">Tous les statuts</option>
              <option value="active">Actifs</option>
              <option value="inactive">Inactifs</option>
              <option value="prospect">Prospects</option>
            </select>
          </div>

          {/* Sprint 6 - Nouveau filtre Type de contact */}
          <div>
            <select
              value={filterTypeContact}
              onChange={(e) => setFilterTypeContact(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
            >
              <option value="">Tout</option>
              <option value="Client potentiel">Client potentiel</option>
              <option value="Client actuel">Client actuel</option>
              <option value="Autre">Autre</option>
            </select>
          </div>
          
          <div>
            <select
              value={filterIndustry}
              onChange={(e) => setFilterIndustry(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
            >
              <option value="">Toutes les industries</option>
              {industries.map(industry => (
                <option key={industry} value={industry}>{industry}</option>
              ))}
            </select>
          </div>

          {/* Sprint 6 - Nouveau filtre Ville */}
          <div>
            <input
              type="text"
              placeholder="Ville..."
              value={filterCity}
              onChange={(e) => setFilterCity(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
            />
          </div>

          {/* Sprint 6 - Nouveau filtre Pays */}
          <div>
            <input
              type="text"
              placeholder="Pays..."
              value={filterPays}
              onChange={(e) => setFilterPays(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
            />
          </div>
          
          <div>
            <select
              value={filterCommercial}
              onChange={(e) => setFilterCommercial(e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
            >
              <option value="">Tous les commerciaux</option>
              {commerciaux.map(commercial => (
                <option key={commercial.id} value={commercial.id}>
                  {commercial.full_name}
                </option>
              ))}
            </select>
          </div>
          
          <div className="flex gap-2">
            <button
              onClick={handleAdvancedSearch}
              className="flex-1 bg-baobab-primary text-white px-4 py-3 rounded-lg hover:bg-baobab-dark transition-colors text-sm"
            >
              🔍
            </button>
            <button
              onClick={resetFilters}
              className="bg-gray-500 text-white px-4 py-3 rounded-lg hover:bg-gray-600 transition-colors text-sm"
            >
              ↺
            </button>
            {/* Sprint 7 - Bouton Export CSV */}
            <button
              onClick={handleExportCSV}
              disabled={exportLoading}
              className="bg-green-600 text-white px-4 py-3 rounded-lg hover:bg-green-700 transition-colors text-sm disabled:opacity-50"
            >
              {exportLoading ? '⏳' : '📄'}
            </button>
          </div>
        </div>
        
        {/* Sort Options */}
        <div className="flex items-center gap-4 text-sm">
          <span className="text-gray-600">Trier par:</span>
          <button
            onClick={() => handleSort('name')}
            className={`flex items-center gap-1 px-3 py-1 rounded ${
              sortField === 'name' ? 'bg-baobab-primary text-white' : 'bg-gray-200'
            }`}
          >
            Nom {getSortIcon('name')}
          </button>
          <button
            onClick={() => handleSort('created_at')}
            className={`flex items-center gap-1 px-3 py-1 rounded ${
              sortField === 'created_at' ? 'bg-baobab-primary text-white' : 'bg-gray-200'
            }`}
          >
            Date {getSortIcon('created_at')}
          </button>
          <button
            onClick={() => handleSort('status')}
            className={`flex items-center gap-1 px-3 py-1 rounded ${
              sortField === 'status' ? 'bg-baobab-primary text-white' : 'bg-gray-200'
            }`}
          >
            Statut {getSortIcon('status')}
          </button>
        </div>
      </div>

      {/* Batch Actions Bar */}
      {showBatchActions && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-blue-700 font-medium">
                {selectedClients.length} société(s) sélectionnée(s)
              </span>
              <button
                onClick={() => setSelectedClients([])}
                className="text-blue-600 hover:text-blue-800 text-sm"
              >
                Tout désélectionner
              </button>
            </div>
            
            <div className="flex gap-2">
              <button
                onClick={() => handleBatchStatusChange('active')}
                className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors text-sm"
              >
                ✓ Marquer Actif
              </button>
              <button
                onClick={() => handleBatchStatusChange('prospect')}
                className="bg-yellow-500 text-white px-4 py-2 rounded-lg hover:bg-yellow-600 transition-colors text-sm"
              >
                👀 Marquer Prospect
              </button>
              <button
                onClick={() => handleBatchStatusChange('inactive')}
                className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors text-sm"
              >
                ⏸ Marquer Inactif
              </button>
              <button
                onClick={handleBatchDelete}
                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition-colors text-sm"
              >
                🗑 Supprimer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Client Form Modal */}
      {showForm && (
        <ClientForm
          client={editingClient}
          onSubmit={handleSubmit}
          onCancel={() => {
            setShowForm(false);
            setEditingClient(null);
          }}
        />
      )}

      {/* Import CSV Modal */}
      <CSVImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        type="companies"
        onImportComplete={reloadData}
      />

      {/* Results */}
      <div className="flex justify-between items-center text-sm text-gray-600 mb-4">
        <span>
          {filteredClients.length} société{filteredClients.length > 1 ? 's' : ''} trouvée{filteredClients.length > 1 ? 's' : ''}
        </span>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={selectedClients.length === filteredClients.length && filteredClients.length > 0}
            onChange={handleSelectAll}
            className="rounded"
          />
          <span>Sélectionner tout</span>
        </div>
      </div>

      {/* Clients List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-baobab-primary text-white">
                <th className="p-3 text-left w-10">
                  <input
                    type="checkbox"
                    checked={selectedClients.length === filteredClients.length && filteredClients.length > 0}
                    onChange={handleSelectAll}
                    className="rounded"
                  />
                </th>
                <th className="p-3 text-left cursor-pointer hover:bg-baobab-dark" onClick={() => handleSort('name')}>
                  Nom {getSortIcon('name')}
                </th>
                <th className="p-3 text-left">Email</th>
                <th className="p-3 text-left">Téléphone</th>
                <th className="p-3 text-left">Ville</th>
                {/* Sprint 6 - Nouvelles colonnes */}
                <th className="p-3 text-left">Type de contact</th>
                <th className="p-3 text-left">Pays</th>
                <th className="p-3 text-left">Industrie</th>
                <th className="p-3 text-left">CC</th>
                <th className="p-3 text-left">Commercial</th>
                <th className="p-3 text-left cursor-pointer hover:bg-baobab-dark" onClick={() => handleSort('status')}>
                  Statut {getSortIcon('status')}
                </th>
                <th className="p-3 text-left">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((client) => (
                <tr key={client.id} className={`border-b border-gray-200 hover:bg-gray-50 ${
                  selectedClients.includes(client.id) ? 'bg-blue-50' : ''
                }`}>
                  <td className="p-3">
                    <input
                      type="checkbox"
                      checked={selectedClients.includes(client.id)}
                      onChange={() => handleSelectClient(client.id)}
                      className="rounded"
                    />
                  </td>
                  <td className="p-3 font-medium">{client.name}</td>
                  <td className="p-3">{client.email || '-'}</td>
                  <td className="p-3">{client.phone || '-'}</td>
                  <td className="p-3">{client.city || '-'}</td>
                  {/* Sprint 6 - Nouvelles données */}
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      client.type_contact === 'Client actuel' ? 'bg-green-100 text-green-800' :
                      client.type_contact === 'Client potentiel' ? 'bg-blue-100 text-blue-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {client.type_contact || 'Client potentiel'}
                    </span>
                  </td>
                  <td className="p-3">{client.pays || 'Côte d\'Ivoire'}</td>
                  <td className="p-3">{client.industry || '-'}</td>
                  <td className="p-3">{client.compte_contribuable || '-'}</td>
                  <td className="p-3">{client.responsable_compte_nom || '-'}</td>
                  <td className="p-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(client.status)}`}>
                      {getStatusLabel(client.status)}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setEditingClient(client);
                          setShowForm(true);
                        }}
                        className="text-blue-600 hover:text-blue-800 px-2 py-1 text-sm"
                      >
                        Modifier
                      </button>
                      <button
                        onClick={() => handleDelete(client.id)}
                        className="text-red-600 hover:text-red-800 px-2 py-1 text-sm"
                      >
                        Supprimer
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredClients.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">🏢</div>
            <p>Aucune société trouvée</p>
            {(searchTerm || filterStatus || filterIndustry || filterCommercial) && (
              <p className="text-sm mt-2">Essayez de modifier vos critères de recherche</p>
            )}
          </div>
        )}

        {/* Pagination */}
        {filteredClients.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(filteredClients.length / itemsPerPage)}
            totalItems={filteredClients.length}
            itemsPerPage={itemsPerPage}
            onPageChange={(page) => setCurrentPage(page)}
            onItemsPerPageChange={(newItemsPerPage) => {
              setItemsPerPage(newItemsPerPage);
              setCurrentPage(1);
            }}
          />
        )}
      </div>
    </div>
  );
};

// Client Form Component (unchanged from original)
const ClientForm = ({ client, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: client?.name || '',
    email: client?.email || '',
    phone: client?.phone || '',
    address: client?.address || '',
    city: client?.city || '',  // Champ existant
    // Sprint 6 - Nouveaux champs
    pays: client?.pays || 'Côte d\'Ivoire',
    type_contact: client?.type_contact || 'Client potentiel',
    rccm: client?.rccm || '',
    contact_person: client?.contact_person || '',
    industry: client?.industry || '',
    status: client?.status || 'active',
    notes: client?.notes || '',
    responsable_compte_id: client?.responsable_compte_id || '',
    responsable_compte_nom: client?.responsable_compte_nom || '',
    compte_contribuable: client?.compte_contribuable || ''
  });

  const [commerciaux, setCommerciaux] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [showContactsSection, setShowContactsSection] = useState(false);

  useEffect(() => {
    fetchCommerciaux();
    if (client?.id) {
      fetchCompanyContacts(client.id);
    }
  }, [client]);

  const fetchCommerciaux = async () => {
    try {
      const response = await axios.get(`${API}/users?role=commercial`);
      setCommerciaux(response.data);
    } catch (error) {
      console.error('Error fetching commerciaux:', error);
    }
  };

  const fetchCompanyContacts = async (companyId) => {
    try {
      const response = await axios.get(`${API}/contacts/company/${companyId}`);
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching company contacts:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    
    if (name === 'responsable_compte_id') {
      const selectedCommercial = commerciaux.find(c => c.id === value);
      setFormData(prev => ({
        ...prev,
        responsable_compte_id: value,
        responsable_compte_nom: selectedCommercial ? selectedCommercial.full_name : ''
      }));
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">
          {client ? 'Modifier la société' : 'Nouvelle société'}
        </h3>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nom *
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Téléphone
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ville
              </label>
              <input
                type="text"
                name="city"
                value={formData.city}
                onChange={handleChange}
                placeholder="Abidjan, Bouaké..."
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
              />
            </div>

            {/* Sprint 6 - Nouveau champ Pays */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pays
              </label>
              <input
                type="text"
                name="pays"
                value={formData.pays}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
              />
            </div>

            {/* Sprint 6 - Nouveau champ Type de contact */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type de contact
              </label>
              <select
                name="type_contact"
                value={formData.type_contact}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
              >
                <option value="Client potentiel">Client potentiel</option>
                <option value="Client actuel">Client actuel</option>
                <option value="Autre">Autre</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                RCCM
              </label>
              <input
                type="text"
                name="rccm"
                value={formData.rccm}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Compte contribuable (CC)
              </label>
              <input
                type="text"
                name="compte_contribuable"
                value={formData.compte_contribuable}
                onChange={handleChange}
                placeholder="Numéro de compte contribuable"
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Personne de contact
              </label>
              <input
                type="text"
                name="contact_person"
                value={formData.contact_person}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Secteur d'activité
              </label>
              <input
                type="text"
                name="industry"
                value={formData.industry}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Statut
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
              >
                <option value="active">Actif</option>
                <option value="inactive">Inactif</option>
                <option value="prospect">Prospect</option>
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Commercial responsable
              </label>
              <select
                name="responsable_compte_id"
                value={formData.responsable_compte_id}
                onChange={handleChange}
                className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
              >
                <option value="">Sélectionner un commercial</option>
                {commerciaux.map(commercial => (
                  <option key={commercial.id} value={commercial.id}>
                    {commercial.full_name} ({commercial.email})
                  </option>
                ))}
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Adresse
            </label>
            <textarea
              name="address"
              value={formData.address}
              onChange={handleChange}
              rows="2"
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="3"
              className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-baobab-primary"
            />
          </div>

          {/* Contacts Section */}
          {client?.id && (
            <div className="border-t border-gray-200 pt-6">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-lg font-medium text-gray-900 flex items-center">
                  <span className="mr-2">👥</span>
                  Contacts de l'entreprise
                </h4>
                <button
                  type="button"
                  onClick={() => setShowContactsSection(!showContactsSection)}
                  className="text-baobab-primary hover:text-baobab-dark"
                >
                  {showContactsSection ? 'Masquer' : 'Afficher'} ({contacts.length})
                </button>
              </div>
              
              {showContactsSection && (
                <div className="space-y-3 max-h-60 overflow-y-auto">
                  {contacts.length > 0 ? (
                    contacts.map((contact) => (
                      <div key={contact.id} className="bg-gray-50 p-3 rounded-lg border border-gray-200">
                        <div className="flex justify-between items-start">
                          <div>
                            <h5 className="font-medium text-gray-900">
                              {contact.prenom} {contact.nom}
                            </h5>
                            <p className="text-sm text-gray-600">{contact.fonction}</p>
                            <p className="text-sm text-gray-600">{contact.email}</p>
                            {contact.telephone && (
                              <p className="text-sm text-gray-600">{contact.telephone}</p>
                            )}
                          </div>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            contact.role === 'Commercial' ? 'bg-blue-100 text-blue-800' :
                            contact.role === 'Technical' ? 'bg-green-100 text-green-800' :
                            contact.role === 'Billing' ? 'bg-orange-100 text-orange-800' :
                            'bg-purple-100 text-purple-800'
                          }`}>
                            {contact.role}
                          </span>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4 text-gray-500">
                      <p>Aucun contact associé à cette société.</p>
                      <p className="text-sm">Vous pouvez en ajouter via l'Annuaire Contacts.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          
          <div className="flex gap-3 pt-4">
            <button
              type="submit"
              className="flex-1 bg-baobab-primary text-white py-3 px-4 rounded-lg hover:bg-baobab-dark transition-colors"
            >
              {client ? 'Modifier' : 'Créer'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 bg-gray-500 text-white py-3 px-4 rounded-lg hover:bg-gray-600 transition-colors"
            >
              Annuler
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ClientsCRMEnhanced;